/**
 * JurisdictionPicker — inline typeahead for the Civic Scraper grid.
 *
 * Reuses the /api/admin/jurisdictions/search endpoint (geo_places + counties)
 * and binds the linked jurisdiction row (which is what the FK requires).
 */
import { useState, useEffect, useRef } from 'react';
import { useJurisdictionSearch } from '@/hooks/useJurisdictionSearch';
import { Input } from '@/components/ui/input';
import { Check, MapPin, X, Pencil } from 'lucide-react';

interface JurisdictionPickerProps {
  value: string | null;
  displayName: string | null;
  onSelect: (id: string, name: string, path: string | null) => void;
  onClear: () => void;
}

export function JurisdictionPicker({
  value,
  displayName,
  onSelect,
  onClear,
}: JurisdictionPickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [pickedPath, setPickedPath] = useState<string | null>(null);
  const { results, loading } = useJurisdictionSearch(query);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const label = displayName ?? (value ? 'Linked jurisdiction' : null);
  const editing = open || label === null;

  function pick(id: string, name: string, path: string | null) {
    onSelect(id, name, path);
    setPickedPath(path);
    setQuery('');
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative min-w-[200px]">
      {!editing ? (
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-800">
            <span className="truncate">{label}</span>
            <span className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                aria-label="Clear jurisdiction"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => {
                  setPickedPath(null);
                  onClear();
                }}
              >
                <X className="w-3 h-3" />
              </button>
              <button
                type="button"
                aria-label="Reassign"
                className="text-blue-500 hover:text-blue-700"
                onClick={() => {
                  setOpen(true);
                  setQuery('');
                  setTimeout(() => inputRef.current?.focus(), 10);
                }}
              >
                <Pencil className="w-3 h-3" />
              </button>
            </span>
          </div>
          {pickedPath ? (
            <span
              className="block font-mono text-[10px] font-medium text-gray-900 leading-relaxed break-all"
              title={pickedPath}
            >
              {pickedPath}
            </span>
          ) : null}
        </div>
      ) : (
        <div>
          <Input
            ref={inputRef}
            placeholder="Search jurisdiction…"
            value={query}
            className="h-7 text-xs"
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' &&
                results.length === 1 &&
                results[0]?.linked_jurisdiction_id
              ) {
                e.preventDefault();
                pick(
                  results[0].linked_jurisdiction_id as string,
                  `${results[0].name}, ${results[0].state}`,
                  results[0].jurisdiction_path ?? null
                );
              }
            }}
          />
          {open && (
            <div className="absolute z-30 mt-1 max-h-56 w-72 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
              {loading ? (
                <div className="px-3 py-2 text-xs text-gray-500">
                  Searching…
                </div>
              ) : results.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400">
                  No jurisdictions found
                </div>
              ) : (
                results.map((r, i) => {
                  const enabled = !!r.linked_jurisdiction_id;
                  return (
                    <button
                      key={`${r.id}-${i}`}
                      type="button"
                      className="flex w-full items-start gap-2 px-3 py-1.5 text-left text-xs hover:bg-blue-50 disabled:opacity-50"
                      disabled={!enabled}
                      title={
                        enabled
                          ? undefined
                          : 'Place exists in geo data but has no jurisdiction row yet'
                      }
                      onClick={() => {
                        if (!r.linked_jurisdiction_id) return;
                        pick(
                          r.linked_jurisdiction_id as string,
                          `${r.name}, ${r.state}`,
                          r.jurisdiction_path ?? null
                        );
                      }}
                    >
                      <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-gray-400" />
                      <span className="flex-1 min-w-0">
                        <span className="font-medium text-gray-800">
                          {r.name}, {r.state}
                        </span>
                        <span className="block text-[10px] text-gray-500">
                          {r.county ? `${r.county} · ` : ''}
                          {r.jurisdiction_type}
                        </span>
                        {r.jurisdiction_path ? (
                          <span
                            className="block font-mono text-[10px] font-medium text-gray-900 truncate"
                            title={r.jurisdiction_path}
                          >
                            {r.jurisdiction_path}
                          </span>
                        ) : null}
                      </span>
                      {enabled ? (
                        <Check className="w-3 h-3 text-green-500 shrink-0 mt-0.5" />
                      ) : null}
                    </button>
                  );
                })
              )}
              <div className="border-t border-gray-100 px-3 py-1 text-[10px] text-gray-400">
                Counties + places in the jurisdiction tree; ✓ = assignable
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
